/**
 * Shared building blocks for the two IVT pipeline PropertyFilter controls:
 *   - Impact page  → IpoPipelinePropertyFilter (GetPipelineBubbles / pipelineFilter)
 *   - Tagged page  → PipelinePropertyFilter    (ListTaggedPipelines / sfdcPipelinePropertyFilter)
 *
 * Both build a one-level AND/OR Cloudscape PropertyFilter over the SAME field
 * shapes (date-range / boolean-flag / enum). This module holds the pieces that
 * were otherwise copy-pasted between them: the date-picker operator form, the
 * operator sets, the Greenfield label formatter, and the hardcoded candidate
 * value pools. (Backend parity: the two pages resolve to one shared
 * PropertyFilterEngine + FieldRegistries since DAT-8617.)
 *
 * Presentational only — each page still owns its own field list (which fields it
 * exposes), query state, and translation to the backend param.
 */
import { useEffect, useState } from 'react';
import { DatePicker, FormField, type PropertyFilterProps } from '@amzn/awsui-components-react';

// ── Date operator popover form ─────────────────────────────────────────────
// Cloudscape PropertyFilter renders the operator's value input inside its own
// popover. For CREATED_DATE / CLOSE_DATE we swap in a Cloudscape DatePicker
// (calendar grid + typed input) instead of the default free-form text box.
// Pattern lifted from Cloudscape's `table-date-filter` example.
//
// Backend contract: a single ISO YYYY-MM-DD string per condition on a scalar
// comparison operator (see impactPipelineFilter.ts / sfdcPipelinePropertyFilter.ts
// + Java ValueKind.DATE). `onChange(null)` clears the token so the caller doesn't
// fire an empty/partial date at the backend.
export function DateForm({
  value,
  onChange,
}: PropertyFilterProps.ExtendedOperatorFormProps<string>) {
  const [dateValue, setDateValue] = useState<string>(value ?? '');

  // Sync back if the outer value is edited/cleared elsewhere.
  useEffect(() => {
    setDateValue(value ?? '');
  }, [value]);

  // Push to Cloudscape only when the value is a full YYYY-MM-DD; partial input
  // (e.g. "2026-") pushes null so the popover keeps waiting on Apply without
  // prematurely wiring a broken filter into the request.
  useEffect(() => {
    if (!dateValue) {
      onChange(null);
      return;
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      onChange(dateValue);
    }
  }, [dateValue, onChange]);

  return (
    <div className="ivt-pipeline-filter-date-form">
      <FormField>
        <DatePicker
          placeholder="YYYY/MM/DD"
          value={dateValue}
          onChange={(e) => setDateValue(e.detail.value)}
          expandToViewport
        />
      </FormField>
    </div>
  );
}

// ── Operator sets ──────────────────────────────────────────────────────────

/** Date operators: `=`/`!=` → in/notIn; `<`/`<=`/`>`/`>=` → scalar range compare.
 *  Each renders `DateForm` in the value popover. `match:'date'` is a client-side
 *  matcher hint (we filter server-side, so purely a contract hint). */
export const DATE_OPERATORS: PropertyFilterProps.FilteringProperty['operators'] = (
  ['=', '!=', '<', '<=', '>', '>='] as const
).map((op) => ({ operator: op, form: DateForm, match: 'date' as const }));

/** Enum-typed (multi-select checkbox) operators for set-membership fields. */
export const ENUM_OPERATORS: PropertyFilterProps.FilteringProperty['operators'] = [
  { operator: '=', tokenType: 'enum' },
  { operator: '!=', tokenType: 'enum' },
];

/** IS_GREENFIELD wire value → display label. The applied token renders the raw
 *  token value ("true"/"false") unless the operator carries a `format` fn — the
 *  `filteringOptions` label only applies to the DROPDOWN, not the applied token
 *  (Cloudscape i18n-utils getValueFormatter). So map here to show Greenfield/
 *  Industry, not true/false. */
export function formatGreenfield(value: unknown): string {
  const vals = Array.isArray(value) ? value : [value];
  return vals.map((v) => (String(v) === 'true' ? 'Greenfield' : 'Industry')).join(', ');
}

/** Enum operators WITH the Greenfield value formatter — for IS_GREENFIELD. */
export const GREENFIELD_OPERATORS: PropertyFilterProps.FilteringProperty['operators'] = [
  { operator: '=', tokenType: 'enum', format: formatGreenfield },
  { operator: '!=', tokenType: 'enum', format: formatGreenfield },
];

/** TAG_MATCHES_HIERARCHY wire value → display label. Same reason as
 *  `formatGreenfield`: the APPLIED token renders the raw value unless the operator
 *  carries `format`; `filteringOptions[].label` only styles the dropdown.
 *
 * ⚠️ POLARITY. The field is named after the DB column, not the business question, and
 * getting it backwards produces plausible-looking wrong data rather than an error
 * (the backend returns identical sets for `notIn ["true"]` and `in ["false"]`):
 *     "true"  = the row's industry DOES claim this tag  → own industry, cross-industry EXCLUDED
 *     "false" = it does not, INCLUDING "no industry claims it at all" (PM's call)
 *
 * The labels name a SCOPE, not a boolean, which is what keeps them readable. An earlier
 * cut used True / False and produced `Exclude Cross-Industry = False` — a token that reads
 * as "exclusion is off" (i.e. unfiltered) while actually meaning "cross-industry rows
 * ONLY". Naming both options after what they SELECT removes that reading entirely:
 *     "true"  → Exclude Cross   (own-industry rows; cross-industry excluded)
 *     "false" → Only Cross      (cross-industry rows only — NOT "no filter")
 * "No filter" has its own representation and needs no label: no token at all, or both
 * values ticked (the BOOL single-value guard drops that condition). Only the display text
 * changes here — the wire values stay "true"/"false".
 */
export function formatTagMatchesHierarchy(value: unknown): string {
  const vals = Array.isArray(value) ? value : [value];
  return vals.map((v) => (String(v) === 'true' ? 'Exclude Cross' : 'Only Cross')).join(', ');
}

/** Enum operators WITH the attribution formatter — for TAG_MATCHES_HIERARCHY. */
export const HIERARCHY_OPERATORS: PropertyFilterProps.FilteringProperty['operators'] = [
  { operator: '=', tokenType: 'enum', format: formatTagMatchesHierarchy },
  { operator: '!=', tokenType: 'enum', format: formatTagMatchesHierarchy },
];

// ── Hardcoded candidate value pools ─────────────────────────────────────────
// Not fetched from the backend (design §3.4): a boolean isn't a searchable pool,
// and the score/stage domains change only alongside a backend change.

/** IS_BIG_BET / HAS_IVT_ACTIVITY: literal true/false (backend parses either case). */
export const BOOLEAN_VALUES: readonly string[] = ['true', 'false'];

/** IS_GREENFIELD: BOOL field whose two values read as domain labels. Wire value
 *  stays "true"/"false"; label is what the user picks. Exactly one may be selected
 *  (both / neither → no constraint, enforced in the *PipelineFilter util). */
export const IS_GREENFIELD_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'true', label: 'Greenfield' },
  { value: 'false', label: 'Industry' },
];

/** TAG_MATCHES_HIERARCHY: BOOL field, exactly one value (both / neither → no
 *  constraint, enforced in the *PipelineFilter utils). Labels are PM's wording — see
 *  formatTagMatchesHierarchy for why True is the own-industry bucket, and for the
 *  reading hazard on False. Wire values stay "true"/"false". */
export const TAG_MATCHES_HIERARCHY_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'true', label: 'Exclude Cross' },
  { value: 'false', label: 'Only Cross' },
];

/**
 * The token both pipeline pages start with: own-industry rows only.
 *
 * PM's default (2026-09-07) — the common case is "my industry's pipeline", and without
 * it a row tagged to another industry's hierarchy shows up in this industry's view.
 *
 * ⚠️ The two buckets are NOT a partition, so this is not "hide N of M rows". Judgement
 * is PER ROW of tagged_pipeline (one opp × one tag), so a single opp can be own-industry
 * on its MFG row and cross-industry on its GAME row, and appears under BOTH buckets.
 * That is the intended semantics (MFG should see it, GAME should not) — do not derive
 * "cross-industry count" as total − own-industry.
 *
 * Returned as a factory, not a shared constant: Cloudscape mutates nothing here today,
 * but two pages holding the same object reference in separate state trees is a footgun.
 */
export function ownIndustryOnlyQuery(): PropertyFilterProps.Query {
  return {
    operation: 'and',
    tokens: [{ propertyKey: 'TAG_MATCHES_HIERARCHY', operator: '=', value: ['true'] }],
  };
}

/**
 * Shared `PropertyFilter` i18n strings.
 *
 * Every one of these has to be present. Cloudscape renders a missing key as blank, so a
 * partial object silently produces an empty group heading or an unlabelled button in the
 * "Edit filter" popover — which is how the token-group buttons shipped blank once before
 * (DAT-8462). Keeping one copy means a control added later cannot start out half-labelled.
 *
 * `filteringAriaLabel` is deliberately NOT here: it names what is being filtered, so each
 * control passes its own alongside this object.
 */
export const PROPERTY_FILTER_I18N: PropertyFilterProps.I18nStrings = {
  dismissAriaLabel: 'Dismiss',
  clearAriaLabel: 'Clear',
  groupValuesText: 'Values',
  groupPropertiesText: 'Properties',
  operatorsText: 'Operators',
  operationAndText: 'and',
  operationOrText: 'or',
  operatorEqualsText: 'Equals',
  operatorDoesNotEqualText: 'Does not equal',
  // Date comparison operator labels (CREATED_DATE / CLOSE_DATE range queries).
  operatorLessText: 'Less than',
  operatorLessOrEqualText: 'Less than or equal',
  operatorGreaterText: 'Greater than',
  operatorGreaterOrEqualText: 'Greater than or equal',
  editTokenHeader: 'Edit filter',
  propertyText: 'Property',
  operatorText: 'Operator',
  valueText: 'Value',
  cancelActionText: 'Cancel',
  applyActionText: 'Apply',
  allPropertiesLabel: 'All properties',
  tokenLimitShowMore: 'Show more',
  tokenLimitShowFewer: 'Show fewer',
  clearFiltersText: 'Clear filters',
  tokenOperatorAriaLabel: 'Boolean operator',
  removeTokenButtonAriaLabel: (token) => `Remove filter ${token.propertyKey ?? ''} ${token.value ?? ''}`,
  enteredTextLabel: (text) => `Use: "${text}"`,
  // Token-group editor labels (enableTokenGroups). Without these the add/remove
  // filter buttons in the "Edit filter" popover render blank (DAT-8462 bug fix).
  groupEditAriaLabel: (group) => `Edit filter group with ${group.tokens.length} conditions`,
  tokenEditorTokenActionsAriaLabel: (token) => `Filter actions for ${token.propertyLabel ?? ''}`,
  tokenEditorTokenRemoveAriaLabel: (token) => `Delete filter ${token.propertyLabel ?? ''}`,
  // "Delete filter" = remove this condition entirely. "Ungroup filter" = pull it out
  // of the AND/OR group (the condition stays, just as a standalone top-level filter).
  // Distinct verbs (Delete vs Ungroup) so the two actions read unambiguously.
  tokenEditorTokenRemoveLabel: 'Delete filter',
  tokenEditorTokenRemoveFromGroupLabel: 'Ungroup filter',
  tokenEditorAddNewTokenLabel: 'Add new filter',
  tokenEditorAddTokenActionsAriaLabel: 'Add filter actions',
  tokenEditorAddExistingTokenAriaLabel: (token) => `Add filter ${token.propertyLabel ?? ''} to group`,
  tokenEditorAddExistingTokenLabel: (token) =>
    `Add filter ${token.propertyLabel ?? ''} ${token.operator ?? ''} ${token.value ?? ''} to group`,
};
